import React, { useState, useEffect } from 'react';
import { makeStyles } from '@mui/styles';
import { useSelector, useDispatch } from 'react-redux';
import RestaurantQRCode from './RestaurantQRCode';
import TableQRCode from './TableQRCode'
import Typography from '@mui/material/Typography';
import Button from "@mui/material/Button";
import Grid from '@mui/material/Grid';
import LinearProgress from "@mui/material/LinearProgress";
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';

import { useHistory } from "react-router-dom";

import { showSuccessSnackbar, showErrorSnackbar } from 'store/actions/snackbarMessage'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
// import { getAllMenus } from 'apis/menuAPIs';

import API from "util/API";
import TableQRCodeEditDialog from './TableQRCodeEditDialog';
import { BackButton } from 'components';
import { mergeQrCodeSettings, updateQrCodeSettingByName } from "./qrCodeSettingsUtils";

const useStyles = makeStyles((theme) => ({

    root: {
        padding: theme.spacing(3)
    },
    card: {
        display: 'flex',
    },
    details: {
        display: 'flex',
        flexDirection: 'column',
    },
    content: {
        flex: '1 0 auto',
    },
    cover: {
        width: 151,
        height: 151,
    },
    controls: {
        display: 'flex',
        alignItems: 'center',
        paddingLeft: theme.spacing(1),
        paddingBottom: theme.spacing(1),
    },
    playIcon: {
        height: 38,
        width: 38,
    },
    generatorCard: {
        marginBottom: theme.spacing(3),
        backgroundColor: theme.palette.primary.light,
        color: theme.palette.primary.contrastText,
    },
    generatorButton: {
        marginTop: theme.spacing(2),
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.grey[100],
        }
    },
    saveButtonContainer: {
        marginBottom: theme.spacing(3),
    }
}));

const initialValues = [{
    name: 'pickup-delivery',
    disabled: false,
    disabledDate: null
},
{
    name: 'takeaway',
    disabled: false,
    disabledDate: null
},
{
    name: 'pickup',
    disabled: false,
    disabledDate: null
},
{
    name: 'delivery',
    disabled: false,
    disabledDate: null
},
{
    name: 'foodcourt',
    disabled: false,
    disabledDate: null
},
{
    name: 'booking',
    disabled: false,
    disabledDate: null
},
{
    name: 'dinein',
    disabled: false,
    disabledDate: null
},
];


const fetchAllTableQrCodes = async () => {
    const response = await API.get('/getQrCodeSettings');
    // Ensure we always return an array
    const data = response.data.qrCodeSettings;
    if (Array.isArray(data)) {
        return data;
    } else {
        return [];
    }
};

const QrCodes = (props) => {
    const classes = useStyles();

    const history = useHistory();
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const restaurantId = props.match.params.restaurantId;
    const restaurant = useSelector((state) => state.restaurant.selectedRestaurant);
    const [uiLoading, setUiLoading] = useState(false);
    const showError = (msg) => dispatch(showErrorSnackbar(msg));
    const showSuccess = (msg) => dispatch(showSuccessSnackbar(msg));
    const user = useSelector((state) => state.account.user);
    const [qrSettings, setQrSettings] = useState(initialValues);
    
    // Edit dialog state
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingQrCode, setEditingQrCode] = useState(null);
    const [editPaymentOption, setEditPaymentOption] = useState('notRequired');
    const [editDisabled, setEditDisabled] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);
    const [neverExpire, setNeverExpire] = useState(false);

    const { data: allQrCodes = [], isLoading: isLoadingQrCodes } = useQuery({
        queryKey: ['allTableQrCodes'],
        queryFn: fetchAllTableQrCodes,
        staleTime: 5 * 60 * 1000,
    });

    const updateQrCodeMutation = useMutation({
        mutationFn: async ({ tableNumber, paymentRequired, expiryTime, disabled }) => {
            const response = await API.put('/updateQrCodeSettings', {
                tableNumber,
                paymentRequired,
                expiryTime,
                disabled
            });
            return response.data;
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['allTableQrCodes'] });
            setEditDialogOpen(false);
            setEditingQrCode(null);
            setSelectedDate(null);
            setNeverExpire(false);
            setEditDisabled(false);
            showSuccess('QR code updated successfully!');
        },
        onError: (error) => {
            showError(error.response?.data?.message || 'Failed to update QR code');
        }
    });

    useEffect(() => {
        // Start from what is saved: the same array also holds the per-table QR
        // settings, which Save must not drop.
        if (restaurant && Array.isArray(restaurant.qrCodeSettings)) {
            setQrSettings(mergeQrCodeSettings(initialValues, restaurant.qrCodeSettings));
        }
    
    }, [restaurant]);


    // const { data: menus = [] } = useQuery({
    //     queryKey: ['menus', restaurant?.id],
    //     queryFn: () => getAllMenus(restaurant?.id),
    //     enabled: !!restaurant?.id,
    // });

    const updateQRCodeSetting = (name, disabled, dateString) => {
        setQrSettings((current) => updateQrCodeSettingByName(current, name, disabled, dateString));
    }


    const updateQRCode = () => {
        if (!restaurant) {
            showError('Please go back to the restaurant screen and try again.');
            return;
        }

        //   console.log('qrSettings', qrSettings);

        setUiLoading(true);
        API.put("/updateRestaurantValues/", {
            qrCodeSettings: qrSettings,
        })
            .then((response) => {
                setUiLoading(false);
                showSuccess('Updated successfully');
                if (user.role === "restaurantAdmin") {
                    history.push("/myRestaurant");
                } else {
                    history.push("/restaurants");
                }
            })
            .catch((error) => {
                if (error.response && error.response.status === 403) {
                    props.history.push("/sign-in");
                }
                console.log(JSON.stringify(error));
                setUiLoading(false);

            });
    };

    const handleOpenEditDialog = (qrCode) => {
        setEditingQrCode(qrCode);
        setEditPaymentOption(qrCode.payment || 'notRequired');
        setEditDisabled(qrCode.disabled || false);
        
        // Set the expiry date if it exists
        if (qrCode.expiresTimestamp) {
            const expiryDate = new Date(parseInt(qrCode.expiresTimestamp));
            setSelectedDate(expiryDate);
            setNeverExpire(false);
        } else {
            setSelectedDate(null);
            setNeverExpire(true);
        }
        
        setEditDialogOpen(true);
    };

    const handleCloseEditDialog = () => {
        setEditDialogOpen(false);
        setEditingQrCode(null);
        setSelectedDate(null);
        setNeverExpire(false);
        setEditDisabled(false);
    };

    const handleUpdateQrCode = () => {
        if (!selectedDate && !neverExpire) {
            showError('Please select an expiry date or choose Never Expire');
            return;
        }
        
        const expiryTime = neverExpire ? null : selectedDate.getTime();
        
        updateQrCodeMutation.mutate({
            tableNumber: editingQrCode.name,
            paymentRequired: editPaymentOption,
            expiryTime: expiryTime,
            disabled: editDisabled
        });
    };

    const formatExpireTime = (timestamp) => {
        if (!timestamp) return '';
        const date = new Date(timestamp);
        return date.toLocaleString();
    };


    const data = [
        {
            name: 'pickup-delivery',
            code: '36f8126f806be0dc9f717f701855a394'
        },
        {
            name: 'takeaway',
            code: 'a0dd323a91c94382bd97261abebd8268'
        },
        {
            name: 'pickup',
            code: '4153876530a8959826e9c0796be24d3a'
        },
        {
            name: 'delivery',
            code: '75687ce9093f8f8a98d85c9f7c36ee57'
        },
        {
            name: 'foodcourt',
            code: 'f28e2bc40c4c98b33f952aed3fdd0d6d'
        },
        {
            name: 'booking',
            code: 'restaurantBooking'
        },
        {
            name: 'queue',
            code: 'queue'
        },
        {
            name: 'Online Menu',
            code: 'onlineMenu'
        }
    ];

    return (
        <div className={classes.root}>
            <BackButton to="/myrestaurant" label="My Restaurant" />
            {restaurant ?
                (
                    <div>
                     {uiLoading ? <LinearProgress /> : null}
                        

                        <Divider style={{ marginBottom: 24 }} />

                        {/* QR Code Settings Section */}
                        <Box mb={3}>
                            <Typography variant="h5" gutterBottom>
                                QR Code Settings
                            </Typography>
                            <Typography variant="body2" color="textSecondary" paragraph>
                                Enable or disable different types of QR codes for your restaurant
                            </Typography>
                        </Box>

                        <Grid item md={6} xs={12} className={classes.saveButtonContainer}>
                            <Button
                                className={classes.submitButton}
                                variant="contained"
                                onClick={updateQRCode}
                                color="primary"
                                disabled={ uiLoading}
                            >
                                Save Settings
                            </Button>
                        </Grid>
                        {data.map(item => {
                            return <RestaurantQRCode restaurantId={restaurantId} item={item} key={item.code} updateQRCodeSetting={updateQRCodeSetting} />;
                        })}
                        <TableQRCode restaurantId={restaurantId} updateQRCodeSetting={updateQRCodeSetting}/>

                        <Divider style={{ marginTop: 32, marginBottom: 32 }} />

                        {/* All Table QR Codes Section */}
                        <Box mb={3}>
                            <Typography variant="h5" gutterBottom>
                                All Table QR Codes
                            </Typography>
                            <Typography variant="body2" color="textSecondary" paragraph>
                                View all table QR codes with short URLs
                            </Typography>
                        </Box>

                        <Card>
                            <CardContent>
                                {isLoadingQrCodes ? (
                                    <LinearProgress />
                                ) : (
                                    <TableContainer component={Paper} variant="outlined">
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell><strong>Table Number</strong></TableCell>
                                                    <TableCell><strong>Payment</strong></TableCell>
                                                    <TableCell><strong>Short URL</strong></TableCell>
                                                    <TableCell><strong>Expires</strong></TableCell>
                                                    <TableCell><strong>Status</strong></TableCell>
                                                    <TableCell><strong>Actions</strong></TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {Array.isArray(allQrCodes) && allQrCodes.filter(qr => !qr.shortUrl).length > 0 ? (
                                                    allQrCodes.filter(qr => !qr.shortUrl).map((qrCode, index) => (
                                                        <TableRow key={index}>
                                                            <TableCell>
                                                                <Typography variant="body2">
                                                                    {qrCode.name || 'N/A'}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography variant="body2">
                                                                    {!qrCode.payment ? 'N/A' : 
                                                                     qrCode.payment === 'required' ? 'Required' : 
                                                                     qrCode.payment === 'optional' ? 'Optional' : 'Not Required'}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                {qrCode.shortUrl ? (
                                                                    <Box display="flex" alignItems="center" gap={1}>
                                                                        <Typography variant="body2" noWrap style={{ maxWidth: 300 }}>
                                                                            {qrCode.shortUrl}
                                                                        </Typography>
                                                                        <IconButton 
                                                                            size="small" 
                                                                            onClick={() => {
                                                                                navigator.clipboard.writeText(qrCode.shortUrl);
                                                                                showSuccess('Short URL copied!');
                                                                            }}
                                                                        >
                                                                            <ContentCopyIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </Box>
                                                                ) : (
                                                                    <Typography variant="body2" color="textSecondary">
                                                                        Not generated
                                                                    </Typography>
                                                                )}
                                                            </TableCell>
                                                            <TableCell>
                                                                {qrCode.expiresTimestamp ? (
                                                                    <Typography variant="body2">
                                                                        {new Date(parseInt(qrCode.expiresTimestamp)).toLocaleString()}
                                                                    </Typography>
                                                                ) : (
                                                                    <Chip 
                                                                        label="Never" 
                                                                        color="default"
                                                                        size="small"
                                                                        variant="outlined"
                                                                    />
                                                                )}
                                                            </TableCell>
                                                            <TableCell>
                                                                <Chip 
                                                                    label={
                                                                        qrCode.disabled ? 'Disabled' :
                                                                        qrCode.expiresTimestamp && Date.now() > parseInt(qrCode.expiresTimestamp) ? 'Expired' : 
                                                                        'Active'
                                                                    } 
                                                                    color={
                                                                        qrCode.disabled ? 'warning' :
                                                                        qrCode.expiresTimestamp && Date.now() > parseInt(qrCode.expiresTimestamp) ? 'error' : 
                                                                        'success'
                                                                    }
                                                                    size="small"
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                <IconButton 
                                                                    size="small" 
                                                                    onClick={() => handleOpenEditDialog(qrCode)}
                                                                    title="Edit QR Code"
                                                                >
                                                                    <EditIcon fontSize="small" />
                                                                </IconButton>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))
                                                ) : (
                                                    <TableRow>
                                                        <TableCell colSpan={6} align="center">
                                                            <Typography variant="body2" color="textSecondary">
                                                                No QR codes settings found
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </CardContent>
                        </Card>

                        {/* Edit QR Code Dialog */}
                        {editingQrCode && (
                            <TableQRCodeEditDialog
                                open={editDialogOpen}
                                onClose={handleCloseEditDialog}
                                tableNumber={editingQrCode.name}
                                editPaymentOption={editPaymentOption}
                                setEditPaymentOption={setEditPaymentOption}
                                editDisabled={editDisabled}
                                setEditDisabled={setEditDisabled}
                                selectedDate={selectedDate}
                                setSelectedDate={setSelectedDate}
                                neverExpire={neverExpire}
                                setNeverExpire={setNeverExpire}
                                onUpdate={handleUpdateQrCode}
                                isUpdating={updateQrCodeMutation.isPending}
                                formatExpireTime={formatExpireTime}
                            />
                        )}
                        
                        </div>
                ) :
                (
                    <Typography color='error'>
                        Please go back to the restaurant screen.
                    </Typography>
                )
            }

        </div>
    );
}


export default QrCodes;